import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  Loader2,
  Users,
  HardHat,
  Briefcase,
  Building2,
  CalendarRange,
  DollarSign,
  Table2,
  ListTree,
  ChevronsUpDown,
  ChevronsDownUp,
} from 'lucide-react';
import PillSelect from '../../../components/UI/PillSelect';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { LeaveTypeBadge } from '../../../components/UI/LeaveTypeBadge';
import AccordionSection from '../../../components/UI/AccordionSection';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import {
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentBadgeColor,
} from '../../../config/departmentConfig';
import toast from '../../../utils/toast';
import api from '../../../services/api';

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

const buildMonthOptions = (first, last) => {
  if (!first || !last) return [];
  const [fy, fm] = first.split('-').map(Number);
  const [ly, lm] = last.split('-').map(Number);
  const out = [];
  let y = fy;
  let m = fm;
  while (y < ly || (y === ly && m <= lm)) {
    const value = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ value, label: monthLabel(value) });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
};

const formatMoney = (val) => {
  if (val == null) return '—';
  return `S$ ${new Intl.NumberFormat('en-SG', { maximumFractionDigits: 0 }).format(val)}`;
};

// Tailwind heat bucket for the Project × Month matrix (no inline styles)
const heatClass = (v, max) => {
  if (!v) return 'text-gray-300';
  const r = max ? v / max : 0;
  if (r < 0.25) return 'bg-blue-50 text-gray-700';
  if (r < 0.5) return 'bg-blue-100 text-gray-800';
  if (r < 0.75) return 'bg-blue-200 text-gray-900';
  return 'bg-blue-300 text-gray-900 font-semibold';
};

const StatCard = ({ label, value, sub, icon: Icon, color = 'red' }) => {
  const colorMap = {
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  };
  const c = colorMap[color] || colorMap.red;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${c.bg}`}>
          <Icon className={`w-6 h-6 ${c.text}`} />
        </div>
        <div className="ml-4 min-w-0">
          <p className="text-2xl font-bold text-gray-900 truncate">{value ?? '-'}</p>
          <p className="text-sm text-gray-600">{label}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

// ---- Project × Month summary --------------------------------------------- //
const ProjectSummaryView = ({ data }) => {
  const labels = data.selected?.month_labels || [];
  const maxMonthly = useMemo(
    () => Math.max(1, ...data.projects.flatMap((p) => p.monthly || [])),
    [data.projects]
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
        <Table2 className="w-4 h-4 text-gray-500" />
        <h3 className="font-semibold text-gray-800">Project × Month — Headcount</h3>
        <span className="ml-auto text-sm font-normal text-gray-500">{data.projects.length} projects</span>
      </div>
      <div className="md:hidden space-y-3 p-4">
        {data.projects.map((p) => (
          <div key={p.code} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 font-mono">{p.code}</p>
                <p className="text-xs text-gray-500 truncate">{p.name}</p>
              </div>
              <span className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">{p.total} total</span>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500">Contract</span>
                <span className="text-gray-900 text-right tabular-nums">
                  {p.contract_value != null ? formatMoney(p.contract_value) : '—'}
                </span>
              </div>
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500">Staff / Workers</span>
                <span className="text-gray-900 text-right tabular-nums">
                  {p.staff || '—'} / {p.workers || '—'}
                </span>
              </div>
              {labels.length > 0 && (
                <div className="pt-1 border-t border-gray-100 space-y-1">
                  {labels.map((l, i) => (
                    (p.monthly || [])[i] ? (
                      <div key={l} className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-gray-500">{l}</span>
                        <span className={`tabular-nums px-1.5 py-0.5 rounded ${heatClass((p.monthly || [])[i], maxMonthly)}`}>
                          {(p.monthly || [])[i]}
                        </span>
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-auto max-h-[640px]">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr className="bg-red-600 text-white text-xs">
              <th className="px-4 py-2 text-left font-medium">Code</th>
              <th className="px-4 py-2 text-left font-medium">Project</th>
              <th className="px-4 py-2 text-right font-medium">Contract (S$)</th>
              {labels.map((l) => (
                <th key={l} className="px-2 py-2 text-center font-medium whitespace-nowrap">{l}</th>
              ))}
              <th className="px-3 py-2 text-right font-medium">Staff</th>
              <th className="px-3 py-2 text-right font-medium">Workers</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.projects.map((p) => (
              <tr key={p.code} className="table-row-dense hover:bg-gray-50">
                <td className="px-4 py-1.5 font-mono font-semibold text-gray-800 whitespace-nowrap">{p.code}</td>
                <td className="px-4 py-1.5 text-gray-700 min-w-[180px]">{p.name}</td>
                <td className="px-4 py-1.5 text-right tabular-nums text-gray-700 whitespace-nowrap">
                  {p.contract_value != null ? formatMoney(p.contract_value) : <span className="text-gray-400">—</span>}
                </td>
                {(p.monthly || []).map((v, i) => (
                  <td key={i} className={`px-2 py-1.5 text-center tabular-nums ${heatClass(v, maxMonthly)}`}>
                    {v || ''}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right tabular-nums">{p.staff || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{p.workers || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900 bg-gray-50">{p.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- Designation summary -------------------------------------------------- //
const DesignationTable = ({ title, rows, icon: Icon, accent, showTrade = false }) => (
  <div>
    <h5 className={`flex items-center gap-1.5 text-xs font-semibold mb-1.5 ${accent}`}>
      <Icon className="w-3.5 h-3.5" /> {title}
    </h5>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-400 italic px-1 py-2">None</p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 text-gray-600 text-xs">
            <th className="px-3 py-1.5 text-left font-medium">Designation</th>
            <th className="px-3 py-1.5 text-left font-medium">Department</th>
            {showTrade && <th className="px-3 py-1.5 text-left font-medium">Trade</th>}
            <th className="px-3 py-1.5 text-right font-medium">#</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((d, i) => (
            <tr key={i} className="table-row-dense">
              <td className="px-3 py-1.5 text-gray-800">{d.designation}</td>
              <td className="px-3 py-1.5 text-gray-600">{d.dept}</td>
              {showTrade && (
                <td className="px-3 py-1.5 text-gray-500">{d.trade || <span className="text-gray-300">—</span>}</td>
              )}
              <td className="px-3 py-1.5 text-right tabular-nums font-medium text-gray-900">{d.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const DesignationView = ({ designation }) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return designation;
    return designation.filter(
      (p) => p.code.toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q)
    );
  }, [designation, q]);

  const allOpen = filtered.length > 0 && filtered.every((p) => expanded[p.code]);
  const toggleAll = () => {
    if (allOpen) {
      setExpanded({});
    } else {
      const next = {};
      filtered.forEach((p) => { next[p.code] = true; });
      setExpanded(next);
    }
  };
  const toggle = (code) => setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));

  return (
    <div className="space-y-3">
      {/* Toolbar: search + expand-all + count */}
      <div className="flex flex-wrap items-center gap-3">
        <DebouncedSearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search project code or name..."
          theme="admin"
          className="max-w-md"
        />
        {!q && (
          <button
            onClick={toggleAll}
            className="icon-button-admin flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={filtered.length === 0}
            title={allOpen ? 'Collapse all' : 'Expand all'}
            aria-label={allOpen ? 'Collapse all projects' : 'Expand all projects'}
          >
            {allOpen ? <ChevronsDownUp className="w-4 h-4" /> : <ChevronsUpDown className="w-4 h-4" />}
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          {filtered.length} of {designation.length} projects
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-10 text-center text-sm text-gray-500">
          No projects match “{search}”.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((proj) => (
            <AccordionSection
              key={proj.code}
              expanded={q ? true : !!expanded[proj.code]}
              onToggle={() => toggle(proj.code)}
              theme="admin"
              contentVariant="padded"
              title={
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-gray-800">{proj.code}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-700 font-normal">{proj.name}</span>
                </div>
              }
              trailing={
                <>
                  <InfoBadge color="blue" label={`${proj.staff_count} staff`} />
                  <InfoBadge color="amber" label={`${proj.worker_count} workers`} />
                  {proj.contract_value != null && (
                    <InfoBadge color="emerald" label={formatMoney(proj.contract_value)} />
                  )}
                </>
              }
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <DesignationTable title="Staff" rows={proj.staff} icon={Briefcase} accent="text-blue-700" />
                <DesignationTable title="Workers" rows={proj.workers} icon={HardHat} accent="text-amber-700" showTrade />
              </div>
            </AccordionSection>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Deployment matrix (staff / worker) ----------------------------------- //
const DeploymentCell = ({ cell, idleLabel }) => {
  if (!cell) return null;
  if (cell.state === 'codes') {
    return (
      <div className="flex flex-col items-center gap-0.5">
        {cell.codes.map((c) => (
          <span key={c} className="font-mono text-[11px] text-gray-800 leading-tight">{c}</span>
        ))}
      </div>
    );
  }
  if (cell.state === 'idle') return <span className="text-xs italic text-gray-400">{idleLabel}</span>;
  if (cell.state === 'leave') {
    const dayTxt = cell.days ? `${cell.days} day${cell.days > 1 ? 's' : ''} this month` : '';
    // Reuse the canonical, color-coded leave chip (same as the PLC report)
    // instead of a bespoke abbreviation so leave identity stays consistent
    // across the app.
    return (
      <span
        className="inline-flex flex-col items-center gap-0.5"
        title={`On ${cell.label}${dayTxt ? ` · ${dayTxt}` : ''}`}
      >
        <LeaveTypeBadge leaveTypeName={cell.label} size="xs" />
        {cell.days ? <span className="text-[10px] text-gray-400 leading-none">{cell.days}d</span> : null}
      </span>
    );
  }
  if (cell.state === 'dash') return <span className="text-gray-300">—</span>;
  if (cell.state === 'prehire') {
    return (
      <span className="text-[10px] italic text-gray-300 leading-tight" title="Not yet employed this month (before join date)">
        Not yet joined
      </span>
    );
  }
  if (cell.state === 'exited') {
    return (
      <span className="text-[10px] italic text-gray-300 leading-tight" title="No longer employed this month (after last working day)">
        Left
      </span>
    );
  }
  return null;
};

// Group consecutive members by trade, preserving the (already trade-sorted)
// order from the API. Used for the Teams department where HR thinks in
// trade buckets ("show me all my Duct crew").
const groupMembersByTrade = (members) => {
  const groups = [];
  members.forEach((m) => {
    const trade = m.trade || 'Unspecified trade';
    const last = groups[groups.length - 1];
    if (last && last.trade === trade) last.rows.push(m);
    else groups.push({ trade, rows: [m] });
  });
  return groups;
};

const DeploymentDeptTable = ({ members, labels, idleLabel, showTrade, groupByTrade = false }) => {
  const colSpan = 4 + (showTrade ? 1 : 0) + labels.length;

  const renderRow = (m, i) => (
    <tr key={m.staff_id} className={`${i % 2 ? 'bg-gray-50/60' : ''} hover:bg-gray-50`}>
      <td className="px-3 py-1.5 font-mono font-semibold text-gray-700 whitespace-nowrap">{m.staff_id}</td>
      <td className="px-3 py-1.5 text-gray-800 whitespace-nowrap">{m.name}</td>
      <td className="px-3 py-1.5 text-gray-600">{m.designation}</td>
      {showTrade && (
        <td className="px-3 py-1.5 text-gray-500">{m.trade || <span className="text-gray-300">—</span>}</td>
      )}
      {m.cells.map((cell, ci) => (
        <td key={ci} className="px-2 py-1.5 text-center align-middle border-l border-gray-100">
          <DeploymentCell cell={cell} idleLabel={idleLabel} />
        </td>
      ))}
      <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-900 bg-gray-50">{m.projects}</td>
    </tr>
  );

  let body;
  if (groupByTrade) {
    let idx = 0;
    body = groupMembersByTrade(members).map((grp) => (
      <React.Fragment key={grp.trade}>
        <tr className="bg-gray-100/80">
          <td colSpan={colSpan} className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {grp.trade}
            <span className="ml-2 font-normal normal-case text-gray-400">
              {grp.rows.length} {grp.rows.length === 1 ? 'person' : 'people'}
            </span>
          </td>
        </tr>
        {grp.rows.map((m) => renderRow(m, idx++))}
      </React.Fragment>
    ));
  } else {
    body = members.map((m, i) => renderRow(m, i));
  }

  return (
    <>
      <div className="md:hidden space-y-3 p-4">
        {members.map((m) => (
          <div key={m.staff_id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                <p className="text-xs text-gray-500 font-mono">{m.staff_id}</p>
              </div>
              <span className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">{m.projects} proj</span>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-500">Designation</span>
                <span className="text-gray-900 text-right truncate min-w-0">{m.designation}</span>
              </div>
              {showTrade && (
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-gray-500">Trade</span>
                  <span className="text-gray-900 text-right truncate min-w-0">{m.trade || '—'}</span>
                </div>
              )}
              {labels.length > 0 && (
                <div className="pt-1 border-t border-gray-100 space-y-1.5">
                  {labels.map((l, ci) => (
                    m.cells[ci] ? (
                      <div key={l} className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-gray-500 flex-shrink-0">{l}</span>
                        <div className="text-right min-w-0">
                          <DeploymentCell cell={m.cells[ci]} idleLabel={idleLabel} />
                        </div>
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-auto max-h-[560px]">
      <table className="w-full text-sm border-separate border-spacing-0">
        <thead className="sticky top-0 z-10">
          <tr className="bg-gray-100 text-gray-600 text-xs">
            <th className="px-3 py-2 text-left font-medium">Staff ID</th>
            <th className="px-3 py-2 text-left font-medium">Name</th>
            <th className="px-3 py-2 text-left font-medium">Designation</th>
            {showTrade && <th className="px-3 py-2 text-left font-medium">Trade</th>}
            {labels.map((l) => (
              <th key={l} className="px-2 py-2 text-center font-medium whitespace-nowrap">{l}</th>
            ))}
            <th className="px-3 py-2 text-right font-medium">Projects</th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
      </div>
    </>
  );
};

const DeploymentView = ({ groups, labels, idleLabel, emptyLabel, showTrade = false }) => {
  const [expanded, setExpanded] = useState({});

  const allOpen = groups && groups.length > 0 && groups.every((g) => expanded[g.dept]);
  const toggleAll = () => {
    if (allOpen) {
      setExpanded({});
    } else {
      const next = {};
      groups.forEach((g) => { next[g.dept] = true; });
      setExpanded(next);
    }
  };
  const toggle = (dept) => setExpanded((prev) => ({ ...prev, [dept]: !prev[dept] }));

  if (!groups || groups.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-10 text-center text-sm text-gray-500">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">{groups.length} departments</span>
        <button
          onClick={toggleAll}
          className="icon-button-admin flex-shrink-0 ml-auto"
          title={allOpen ? 'Collapse all' : 'Expand all'}
          aria-label={allOpen ? 'Collapse all departments' : 'Expand all departments'}
        >
          {allOpen ? <ChevronsDownUp className="w-4 h-4" /> : <ChevronsUpDown className="w-4 h-4" />}
        </button>
      </div>
      <div className="space-y-2">
        {groups.map((g) => {
          const Icon = getDepartmentIcon(g.dept);
          const c = getDepartmentColor(g.dept);
          return (
            <AccordionSection
              key={g.dept}
              expanded={!!expanded[g.dept]}
              onToggle={() => toggle(g.dept)}
              theme="admin"
              icon={<Icon className={`w-4 h-4 ${c.text}`} />}
              iconBg={c.bg}
              title={<span className="font-semibold text-gray-800">{g.dept}</span>}
              trailing={
                <InfoBadge
                  color={getDepartmentBadgeColor(g.dept)}
                  label={`${g.members.length} ${g.members.length === 1 ? 'person' : 'people'}`}
                />
              }
            >
              <DeploymentDeptTable
                members={g.members}
                labels={labels}
                idleLabel={idleLabel}
                showTrade={showTrade}
                groupByTrade={g.dept === 'Teams' && showTrade}
              />
            </AccordionSection>
          );
        })}
      </div>
    </div>
  );
};

const SUB_TABS = [
  { id: 'projects', label: 'Project Summary', icon: Table2 },
  { id: 'designation', label: 'Designation Summary', icon: ListTree },
  { id: 'staff', label: 'Staff Deployment', icon: Users },
  { id: 'workers', label: 'Worker Deployment', icon: HardHat },
];

const ProjectManpowerDeploymentPage = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Active sub-tab is persisted in the URL (?tab=) so it survives refresh / sharing.
  const tabParam = searchParams.get('tab');
  const view = SUB_TABS.some((t) => t.id === tabParam) ? tabParam : 'projects';
  const setView = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const monthOptions = useMemo(() => {
    const avail = data?.available;
    return buildMonthOptions(avail?.first, avail?.last);
  }, [data?.available]);

  const fetchSummary = useCallback(async (s, e) => {
    setLoading(true);
    try {
      const params = {};
      if (s) params.start = s;
      if (e) params.end = e;
      const res = await api.get('/reporting/manpower-deployment/', { params });
      setData(res.data);
      if (res.data?.selected) {
        if (!s) setStart(res.data.selected.start);
        if (!e) setEnd(res.data.selected.end);
      }
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load report';
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleStartChange = (val) => {
    setStart(val);
    const e = end && val > end ? val : end;
    if (e !== end) setEnd(e);
    fetchSummary(val, e);
  };

  const handleEndChange = (val) => {
    setEnd(val);
    const s = start && val < start ? val : start;
    if (s !== start) setStart(s);
    fetchSummary(s, val);
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get('/reporting/manpower-deployment/export/', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const span = `${(start || 'start').replace('-', '')}-${(end || 'end').replace('-', '')}`;
      link.setAttribute('download', `Project_Manpower_Deployment_${span}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel file downloaded');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [start, end]);

  const headline = data?.headline;
  const hasData = data?.has_data;
  const labels = data?.selected?.month_labels || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Link
            to="/admin/reports"
            aria-label="Back to Reports"
            title="Back to Reports"
            className="icon-button-admin flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Project Manpower Deployment</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-14">
          Who is deployed on which client project, month by month — Project · DfMA · Safety · Design &amp; BIM · Teams
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">From</span>
          <PillSelect
            options={monthOptions}
            value={start}
            onChange={handleStartChange}
            placeholder="Start month"
            icon={CalendarRange}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">To</span>
          <PillSelect
            options={monthOptions}
            value={end}
            onChange={handleEndChange}
            placeholder="End month"
            icon={CalendarRange}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || loading || !hasData}
          className="icon-button-admin flex-shrink-0 ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
          title={exporting ? 'Exporting...' : 'Download Excel'}
          aria-label={exporting ? 'Exporting Excel' : 'Download Excel'}
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading deployment data...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !hasData && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileSpreadsheet className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No deployment data</h3>
          <p className="text-sm text-gray-500">
            There is no client-project deployment recorded for the selected period.
          </p>
        </div>
      )}

      {/* Report */}
      {!loading && hasData && (
        <div className="space-y-6">
          {data?.selected?.label && (
            <p className="text-sm text-gray-500 -mt-2">
              Showing <span className="font-medium text-gray-700">{data.selected.label}</span> · complete months with reliable clock-in data
            </p>
          )}

          {/* Headline cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Projects" value={headline?.projects} icon={Building2} color="red" />
            <StatCard
              label="Personnel deployed"
              value={headline?.personnel}
              sub={`${headline?.staff || 0} staff · ${headline?.workers || 0} workers`}
              icon={Users}
              color="blue"
            />
            <StatCard
              label="Combined contract value"
              value={formatMoney(headline?.contract_total)}
              sub={headline?.contract_total ? `${headline?.contract_matched} matched to tender portal` : 'tender portal unavailable'}
              icon={DollarSign}
              color="emerald"
            />
            {/* Monthly headcount — last 3 months, as the 4th summary card */}
            <div className="bg-white rounded-2xl shadow-sm border p-5">
              <div className="flex items-center gap-1.5 mb-2.5">
                <CalendarRange className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-xs font-medium text-gray-500">Monthly headcount</p>
              </div>
              {(() => {
                const last3 = data.monthly.slice(-3);
                const colsClass = last3.length >= 3 ? 'grid-cols-3' : last3.length === 2 ? 'grid-cols-2' : 'grid-cols-1';
                return (
                  <div className={`grid ${colsClass} divide-x divide-gray-100`}>
                    {last3.map((m) => (
                      <div key={m.month} className="px-2 text-center first:pl-0 last:pr-0">
                        <p className="text-[11px] text-gray-400 whitespace-nowrap">{m.label}</p>
                        <p className="text-xl font-bold text-gray-900 tabular-nums leading-tight mt-0.5">{m.personnel}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{m.projects} proj</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Sub-tabs — mirrors the Excel tabs */}
          <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide">
            {SUB_TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setView(t.id)}
                  className={`tab-btn-admin ${view === t.id ? 'active' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {view === 'projects' && <ProjectSummaryView data={data} />}
          {view === 'designation' && <DesignationView designation={data.designation} />}
          {view === 'staff' && (
            <DeploymentView
              groups={data.deployment?.staff}
              labels={labels}
              idleLabel="Office"
              emptyLabel="No staff-classified employees in these departments."
            />
          )}
          {view === 'workers' && (
            <DeploymentView
              groups={data.deployment?.workers}
              labels={labels}
              idleLabel="Factory"
              emptyLabel="No worker-classified employees in these departments."
              showTrade
            />
          )}

          <p className="text-xs text-gray-400">
            Deployment = a clock-in on the project site OR an approved External Activity (site walk / meeting / off-site work) tagged to the project that month.
            On the deployment tabs, <span className="italic">Office</span>/<span className="italic">Factory</span> = at overhead with no project clock-in that month; a <span className="font-medium">leave chip</span> (e.g. National Service Leave, Home Leave) marks a month spent entirely on approved leave; <span className="italic text-gray-400">Not yet joined</span>/<span className="italic text-gray-400">Left</span> = outside the person's employment dates; <span className="font-mono">—</span> = employed but no record (unexplained). In Worker Deployment, Teams are grouped by trade (staff are not assigned a trade).
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectManpowerDeploymentPage;
